"use client";

import BlurredPlaceholder from "@/app/(components)/generics/BlurredPlaceholder/BlurredPlaceholder";
import Button, { ButtonType } from "@/app/(components)/generics/Button/Button";
import NextStepCard from "@/app/(components)/generics/cards/NextStepCard/NextStepCard";
import SourceCard from "@/app/(components)/generics/cards/SourceCard/SourceCard";
import IconArrowRightGradient from "@/app/(components)/generics/Icons/IconArrowRightGradient";
import IconSparkles from "@/app/(components)/generics/Icons/IconSparkles";
import IconUser from "@/app/(components)/generics/Icons/IconUser";
import TabbedContent from "@/app/(components)/generics/TabbedContent/TabbedContent";
import { ModalStore } from "@/app/stores/ModalStore";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { DefaultProps } from "@/app/types/common/DefaultProps";
import { URLs } from "@/app/utils/URLs";
import { Limits } from "@/app/vars/Limits";
import { Routes } from "@/app/vars/Routes";
import styles from "./OffersTeaser.module.css";

type Props = DefaultProps;

export default function OffersTeaser(props: Props) {
  const { params } = props;
  const T = useT();

  const openSignUp = () => ModalStore.getState().openSignUp(params);

  const signUpButton = (label: string) => (
    <Button min={true} isLink={false} type={ButtonType.PRIMARY} text={label} onClick={openSignUp} />
  );

  return (
    <div className={styles.teaser}>
      <div className={styles.header}>
        <span className={styles.count}>0 / {Limits.MAX_PROJECT_OFFERS}</span>
        <Button
          min={true}
          isLink={false}
          type={ButtonType.OUTLINE}
          text={T(TKey.PROJECT_OFFER_ADD) as string}
          onClick={openSignUp}
        />
      </div>

      <div className={styles.cards}>
        <SourceCard
          title={T(TKey.OFFERS_EXTRACT_TITLE) as string}
          badge={T(TKey.OFFERS_EXTRACT_BADGE) as string}
          description={T(TKey.OFFERS_EXTRACT_DESCRIPTION) as string}
          benefits={[
            T(TKey.OFFERS_EXTRACT_BENEFIT_1) as string,
            T(TKey.OFFERS_EXTRACT_BENEFIT_2) as string,
            T(TKey.OFFERS_EXTRACT_BENEFIT_3) as string,
          ]}
          actions={signUpButton(T(TKey.OFFERS_EXTRACT_CTA) as string)}
        />

        <SourceCard
          icon={<IconSparkles size={26} />}
          title={T(TKey.OFFERS_GENERATE_TITLE) as string}
          badge={T(TKey.OFFERS_GENERATE_BADGE) as string}
          description={T(TKey.OFFERS_GENERATE_DESCRIPTION) as string}
          benefits={[
            T(TKey.OFFERS_GENERATE_BENEFIT_1) as string,
            T(TKey.OFFERS_GENERATE_BENEFIT_2) as string,
            T(TKey.OFFERS_GENERATE_BENEFIT_3) as string,
          ]}
          actions={signUpButton(T(TKey.OFFERS_GENERATE_CTA) as string)}
        />
      </div>

      <div className={styles.grid}>
        <div className={styles.card}>
          <BlurredPlaceholder
            rows={[
              { cells: [{ width: "62%" }] },
              {
                cells: [
                  { width: "84px", pill: true },
                  { width: "64px", pill: true },
                ],
              },
            ]}
          />

          <div className={styles.transform}>
            <div className={styles.block}>
              <span className={styles.blockLabel}>{T(TKey.OFFER_PROBLEM_LABEL)}</span>
              <BlurredPlaceholder
                rows={[{ cells: [{ width: "92%" }] }, { cells: [{ width: "70%" }] }]}
              />
            </div>
            <div className={styles.arrow}>
              <IconArrowRightGradient size={18} />
            </div>
            <div className={`${styles.block} ${styles.blockPromise}`}>
              <span className={styles.blockLabel}>{T(TKey.OFFER_PROMISE_LABEL)}</span>
              <BlurredPlaceholder
                rows={[{ cells: [{ width: "88%" }] }, { cells: [{ width: "76%" }] }]}
              />
            </div>
          </div>

          <TabbedContent
            items={[
              {
                id: TKey.OFFER_PROOF_LABEL,
                title: T(TKey.OFFER_PROOF_LABEL) as string,
                content: (
                  <div className={styles.tabPanel}>
                    <BlurredPlaceholder
                      rows={[{ cells: [{ width: "90%" }] }, { cells: [{ width: "72%" }] }]}
                    />
                  </div>
                ),
              },
              {
                id: TKey.OFFER_KEY_ADVANTAGES_LABEL,
                title: T(TKey.OFFER_KEY_ADVANTAGES_LABEL) as string,
                content: (
                  <div className={styles.tabPanel}>
                    <BlurredPlaceholder
                      rows={[{ cells: [{ width: "86%" }] }, { cells: [{ width: "68%" }] }]}
                    />
                  </div>
                ),
              },
            ]}
            hideContentPadding={true}
            disableScrollOnTabChange={true}
          />
        </div>

        <NextStepCard
          params={params}
          icon={<IconUser />}
          title={T(TKey.OFFERS_NEXT_AVATAR_TITLE) as string}
          description={T(TKey.OFFERS_NEXT_AVATAR_DESCRIPTION) as string}
          ctaLabel={T(TKey.OFFERS_NEXT_AVATAR_CTA) as string}
          href={URLs.pathOf(Routes.PROTECTED.CLIENT_AVATAR)}
          className={styles.gridNext}
        />
      </div>
    </div>
  );
}
